import { adjustPosition, AllowOffScreen } from "@client/hocs/draggable/lib";
import { getScreenRect } from "@client/lib/coords";
import { type LeftTop } from "@client/types";
import { type Rect } from "@common/declarations";
import * as React from "react";

export interface DraggableHOCProps {
  allowOffBounds?: number;
  boundsSelector?: string;
  initialPosition?: LeftTop;
  minTop?: number;
  shouldResetPosition?: boolean;
  onDragStart?: (el: Element, position: LeftTop, e: MouseEvent) => void;
  onDragged: (position: LeftTop, e: MouseEvent) => void;
  onDragEnd?: (el: Element, position: LeftTop, e: MouseEvent) => void;
}

export type DraggableHOCComponentProps = {
  isDragging: boolean;
};

type State = {
  isDragging: boolean;
};
export default <OriginalProps extends {}>(
  Cmp: React.ComponentType<OriginalProps & DraggableHOCComponentProps>,
  handleSelector?: string
) =>
  class Draggable extends React.PureComponent<OriginalProps & DraggableHOCProps, State> {
    private _ref: React.RefObject<any> = React.createRef();
    private _isStart: boolean = true;
    private _x: number;
    private _y: number;

    public state: State = {
      isDragging: false
    };

    public componentDidMount() {
      const handle = this._getHandle();
      if (handle) {
        handle.style.cursor = "move";
        handle.addEventListener("mousedown", this._onMouseDown);
        window.addEventListener("resize", this._onResize);
      }

      const { initialPosition } = this.props;
      if (initialPosition) {
        const bounds = this._getBounds();

        this._setContainerPosition({
          left: bounds.left + initialPosition.left,
          top: bounds.top + initialPosition.top
        });
      }

      this._ensureInBounds();
    }

    public componentWillUnmount() {
      const handle = this._getHandle();
      if (handle) {
        handle.style.cursor = "";
        handle.removeEventListener("mousedown", this._onMouseDown);
      }

      window.removeEventListener("resize", this._onResize);

      if (this.state.isDragging) {
        document.removeEventListener("mousemove", this._onMouseMove);
        document.removeEventListener("mouseup", this._onMouseUp);
      }
    }

    public render() {
      const {
        allowOffBounds: allowOffScreen,
        initialPosition,
        onDragged,
        onDragEnd,
        onDragStart,
        boundsSelector: parentSelector,
        ...props
      } = this.props;
      // TODO: TS issue
      return <Cmp ref={this._ref} {...(props as any)} isDragging={this.state.isDragging} />;
    }

    private _onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.classList.contains("no_drag") || target.closest(".no_drag")) return;

      const container = this._getContainer();

      const rect = container.getBoundingClientRect();
      this._isStart = true;
      this._x = e.clientX - rect.left;
      this._y = e.clientY - rect.top;

      document.addEventListener("mousemove", this._onMouseMove);
      document.addEventListener("mouseup", this._onMouseUp);
    };

    private _onMouseMove = (e: MouseEvent) => {
      const left = e.clientX - this._x;
      const top = e.clientY - this._y;

      if (!this._isStart || Math.abs(left) > 3 || Math.abs(top) > 3) {
        if (!this.state.isDragging) {
          const container = this._getContainer();
          this.props.onDragStart && this.props.onDragStart(container, { left: this._x, top: this._y }, e);

          this.setState({ isDragging: true });
        }

        this._isStart = false;
        this._setContainerPosition({ left, top });

        this.props.onDragged({ left, top }, e);
      }
    };

    private _onMouseUp = (e: MouseEvent) => {
      document.removeEventListener("mousemove", this._onMouseMove);
      document.removeEventListener("mouseup", this._onMouseUp);

      if (this.state.isDragging) {
        this.setState({ isDragging: false });

        if (this.props.onDragEnd) {
          const left = e.clientX - this._x;
          const top = e.clientY - this._y;

          this.props.onDragEnd(this._getContainer(), { left, top }, e);
        }

        if (this.props.shouldResetPosition) {
          const container = this._getContainer();

          container.style.removeProperty("left");
          container.style.removeProperty("top");
        }
      }
    };

    private _onResize = (e: Event) => this._ensureInBounds();

    private _ensureInBounds = () => {
      const rect = this._getContainer().getBoundingClientRect();
      const bounds = this._getBounds();

      if (rect.right > bounds.right || rect.bottom > bounds.bottom) {
        this._setContainerPosition({
          left: Math.min(rect.left, bounds.right - rect.width),
          top: Math.min(rect.top, bounds.bottom - rect.height)
        });
      }
    };

    private _getContainer = (): HTMLElement => {
      return this._ref.current as HTMLElement;
    };
    private _getBounds = (): Rect => {
      const parent = this.props.boundsSelector
        ? (this._getContainer().closest(this.props.boundsSelector) as HTMLElement)
        : null;
      return parent
        ? {
            top: parent.offsetTop,
            left: parent.offsetLeft,
            bottom: parent.clientHeight,
            right: parent.clientWidth,
            height: parent.clientHeight,
            width: parent.clientWidth
          }
        : getScreenRect();
    };

    private _getHandle = (): HTMLElement | null => {
      const container = this._getContainer();
      return handleSelector ? container.querySelector(handleSelector) : container;
    };

    private _setContainerPosition = (lt: LeftTop) => {
      const container = this._getContainer();

      const alt = adjustPosition(
        lt,
        container.getBoundingClientRect(),
        this._getBounds(),
        this.props.allowOffBounds || AllowOffScreen.None
      );

      Object.assign(container.style, {
        left: `${alt.left}px`,
        top: `${Math.max(this.props.minTop ?? 0, alt.top)}px`
      });
    };
  };
